// The uplink raster is fixed: JBS rift_participant (JBS#3102) declares the
// guest flow as 1280x720 @ 30 and does not scale — a frame at any other
// raster is dropped there and counted. So the client, not the camera,
// decides the raster: frames that arrive at another size are letterboxed
// (aspect kept, black bars) to 1280x720, and cameras faster than 30 fps are
// decimated. A camera slower than 30 fps is sent at its own rate; frames are
// never invented.
export const UPLINK_WIDTH = 1280;
export const UPLINK_HEIGHT = 720;
export const UPLINK_FPS = 30;
/**
 * Frame-rate floor asked of the camera alongside the ideal 30. By constraint
 * fitness, Chrome would otherwise pick an exact 1280x720 mode at 10 fps (a
 * USB2 webcam's uncompressed 720p) over 640x480 at 30. Motion matters more
 * than pixels in a PIP, and the letterbox restores the raster. A floor no
 * mode can meet fails getUserMedia, so `openUplinkCapture` drops it then.
 * It picks the mode; it cannot stop a camera slowing down in low light.
 */
export const CAPTURE_MIN_FPS = 24;
export function uplinkVideoConstraints(cameraId, minFps = CAPTURE_MIN_FPS) {
    return {
        deviceId: cameraId ? { exact: cameraId } : undefined,
        width: { ideal: UPLINK_WIDTH },
        height: { ideal: UPLINK_HEIGHT },
        frameRate: minFps === null ? { ideal: UPLINK_FPS } : { ideal: UPLINK_FPS, min: minFps },
    };
}
/** getUserMedia with the frame-rate floor, retried without it on OverconstrainedError. */
export async function openUplinkCapture(getUserMedia, cameraId, audio) {
    try {
        return await getUserMedia({ video: uplinkVideoConstraints(cameraId), audio });
    }
    catch (err) {
        if (err?.name !== 'OverconstrainedError')
            throw err;
        return getUserMedia({ video: uplinkVideoConstraints(cameraId, null), audio });
    }
}
/** Largest rect with the source aspect that fits `dst`, centred. */
export function letterbox(srcW, srcH, dstW = UPLINK_WIDTH, dstH = UPLINK_HEIGHT) {
    const scale = Math.min(dstW / srcW, dstH / srcH);
    const width = Math.round(srcW * scale);
    const height = Math.round(srcH * scale);
    return { x: Math.floor((dstW - width) / 2), y: Math.floor((dstH - height) / 2), width, height };
}
/**
 * Admit at most `fps` frames per second by capture timestamp (µs). Tolerates
 * capture jitter: a frame is due when it is within a quarter interval of the
 * next slot, and the slot advances from the ideal grid, not the frame, so a
 * steady 60 fps source yields a steady 30.
 */
export class FrameDecimator {
    nextDueUs = null;
    intervalUs;
    constructor(fps = UPLINK_FPS) {
        this.intervalUs = 1_000_000 / fps;
    }
    admit(timestampUs) {
        if (this.nextDueUs === null || timestampUs >= this.nextDueUs - this.intervalUs / 4) {
            const base = this.nextDueUs === null ? timestampUs : Math.max(this.nextDueUs, timestampUs - this.intervalUs);
            this.nextDueUs = base + this.intervalUs;
            return true;
        }
        return false;
    }
}
//# sourceMappingURL=raster.js.map